import clsx from 'clsx';
import Box from '@mui/system/Box';
import Grid from '@mui/material/Grid';
import styled from '@mui/styles/styled';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import makeStyles from '@mui/styles/makeStyles';
import AreaRowComponent from './AreaRowComponent';
import { addEmptyArea } from '../store/areasSlice';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { MoreOptionDialog } from '@blackhyve/common';
import { useDispatch, useSelector } from 'react-redux';
import { Fragment, useContext, useState } from 'react';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import DragIndicator from '@mui/icons-material/DragIndicator';
import ColorDropdown from '../../../components/ColorDropdown';
import TaskIconComponent from '@blackhyve/common/src/icons/Task';
import CopyIconComponent from '@blackhyve/common/src/icons/Copy';
import DeleteIconComponent from '@blackhyve/common/src/icons/Delete';
import InlineEditCardTitle from 'components/common/v3/InlineEditCardTitle';
import LocationLevelIconComponent from '@blackhyve/common/src/icons/LocationLevel';
import DisplayWithEditAccess, { EditContext } from 'components/common/v3/DisplayWithEditAccess';
import {
  createZone,
  deleteZone,
  duplicateZone,
  removeEmptyZone,
  selectZoneById,
  updateZone,
} from '../store/zonesSlice';

const TaskListCountBox = styled(Box)(({ theme }) => ({
  marginTop: 'auto',
  marginBottom: 'auto',
  fontWeight: 'bold',
  cursor: 'pointer',
  verticalAlign: 'middle',
  display: 'inline-flex',
  paddingLeft: '0px',
  paddingRight: '5px',
  borderRadius: '3px',
  marginLeft: '15px',
  paddingTop: '0px',
  paddingBottom: '0px',
  height: '19px',
  border: '0.5px solid',
  borderColor: '#b3b3b3',
  boxSizing: 'border-box',
}));

export const RowContainer = styled(Grid)(({ theme }) => ({
  height: '40px',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
}));

const ZoneContainer = styled(Grid)(({ theme }) => ({
  border: '.5px solid #b3b3b3',
  borderTop: 'none',
  borderRadius: '.5px',
  backgroundColor: 'white',
  boxShadow: theme.shadows[2],
  '&:first-child': {
    borderTop: '.5px solid #b3b3b3',
  },
}));

const TaskCountLabel = styled('span')(({ theme }) => ({
  fontSize: '10px',
  padding: '1px',
  color: 'rgba(0, 0, 0, 0.6)',
  fontWeight: '500',
  cursor: 'pointer',
  margin: 'auto',
}));

const ZoneRowComponent = ({ projectId, id, index }) => {
  const canEdit = useContext(EditContext);
  const [collapse, setCollapse] = useState(true);
  const areas = useSelector((state) => selectZoneById(state, id)?.areas);
  return (
    <Draggable
      draggableId={`zone-${id}`}
      index={index}
      isDragDisabled={!canEdit}
      key={`zone-${id}`}
    >
      {(provided, snapshot) => (
        <ZoneContainer
          ref={provided.innerRef}
          {...provided.draggableProps}
          container
          style={{ ...provided.draggableProps.style }}
        >
          <HeadingComponent
            collapse={collapse}
            dragHandleProps={provided.dragHandleProps}
            id={id}
            projectId={projectId}
            setCollapse={setCollapse}
          />
          <Droppable droppableId={'zone-' + id} type={`AREA-` + id}>
            {(provided) => (
              <Grid container {...provided.droppableProps} ref={provided.innerRef}>
                {collapse && areas && areas.length > 0
                  ? areas.map((areaId, index) => {
                      return (
                        <Draggable
                          draggableId={`area-${areaId}`}
                          index={index}
                          isDragDisabled={!canEdit}
                          key={`area-${areaId}`}
                        >
                          {(provided, snapshot) => (
                            <Grid
                              {...provided.draggableProps}
                              container
                              ref={provided.innerRef}
                              style={
                                snapshot.isDragging
                                  ? {
                                      border: '.5px solid #b3b3b3',
                                      ...provided.draggableProps.style,
                                    }
                                  : {
                                      borderTop: '.5px solid #b3b3b3',
                                      borderBottom: '.5px solid transparent',
                                      ...provided.draggableProps.style,
                                    }
                              }
                            >
                              <AreaRowComponent
                                dragHandleProps={provided.dragHandleProps}
                                id={areaId}
                                key={`area-row-items-${areaId}`}
                              />
                            </Grid>
                          )}
                        </Draggable>
                      );
                    })
                  : null}
                {provided.placeholder}
              </Grid>
            )}
          </Droppable>
        </ZoneContainer>
      )}
    </Draggable>
  );
};

const HeadingComponent = ({ id, dragHandleProps, collapse, setCollapse }) => {
  const dispatch = useDispatch();

  const canEdit = useContext(EditContext);

  const [loading, setLoading] = useState(false);
  const [openMoreOptionDialog, setOpenMoreOptionDialog] = useState(null);
  const color = useSelector((state) => selectZoneById(state, id)?.color);
  const name = useSelector((state) => selectZoneById(state, id)?.name);
  const areas = useSelector((state) => selectZoneById(state, id)?.areas);
  const createdAt = useSelector((state) => selectZoneById(state, id)?.created_at);
  const priority = useSelector((state) => selectZoneById(state, id)?.priority);
  const locationId = useSelector((state) => selectZoneById(state, id)?.location_id);

  const handleAddArea = () => {
    dispatch(addEmptyArea({ id: Date.now() * -1, zoneId: id }));
  };
  const handleUpdateZone = ({ name: newName }) => {
    setLoading(true);
    if (id < 0) {
      dispatch(createZone({ name: newName, locationId: locationId, id })).then(() =>
        setLoading(false)
      );
    } else {
      dispatch(updateZone({ name: newName, id, locationId: locationId })).then(() =>
        setLoading(false)
      );
    }
  };

  const handleDelete = () => {
    setLoading(true);
    if (createdAt) {
      dispatch(deleteZone({ id, locationId })).then(() => setLoading(false));
    } else {
      dispatch(removeEmptyZone({ id, locationId }));
      setLoading(false);
    }
  };

  const handleDuplicate = () => {
    setLoading(true);
    dispatch(duplicateZone({ id, locationId, priority })).then(() => setLoading(false));
  };

  const optionMenu = [
    {
      name: 'Duplicate Zone',
      icon: <CopyIconComponent />,
      action: handleDuplicate,
    },
    {
      name: 'Delete Zone',
      icon: <DeleteIconComponent color="red" />,
      action: handleDelete,
      style: { color: 'red' },
    },
  ];

  const handleOpenMoreOptionDialog = (event) => {
    setOpenMoreOptionDialog(event.target);
  };

  const handleCloseMoreOptionDialog = () => {
    setOpenMoreOptionDialog(null);
  };

  const handleSaveColor = (selectedColor) => {
    setLoading(true);
    dispatch(updateZone({ id, color: selectedColor })).then(() => setLoading(false));
  };

  const requestParameters = {
    id,
  };

  return (
    <RowContainer container item>
      {loading ? (
        <Skeleton className="skeleton-row" variant="text" />
      ) : (
        <Fragment>
          <Grid container item alignItems={'center'} xs={7}>
            <Grid item {...dragHandleProps}>
              <DragIndicator sx={{ color: 'grey.600' }} />
            </Grid>
            <Grid item>
              {areas?.length > 0 ? (
                <IconButton
                  aria-label="Filter"
                  className="iconImage"
                  disableFocusRipple={true}
                  disableRipple={true}
                  size="medium"
                  sx={{ padding: 0 }}
                  onClick={() => setCollapse(!collapse)}
                >
                  {collapse ? <ArrowDropDown /> : <ArrowRightIcon />}
                </IconButton>
              ) : null}
            </Grid>
            <Grid item>
              <LocationLevelIconComponent
                height={16}
                style={{ padding: '5px', display: 'block' }}
              />
            </Grid>
            <Grid item className="card-title">
              <InlineEditCardTitle
                fieldName={'name'}
                handleUpdate={handleUpdateZone}
                isDefaultEdit={id < 0}
                isEditable={canEdit}
                requestParameters={requestParameters}
                textToDisplay={name}
              />
              <TaskListCountBox>
                <IconButton
                  className={'task-list-icon'}
                  disableFocusRipple={true}
                  disableRipple={true}
                  size="small"
                >
                  <TaskIconComponent className="task-image" height={11} width={11} />
                </IconButton>
                <Typography
                  sx={{
                    fontSize: '10px',
                    color: 'grey.700',
                    cursor: 'pointer',
                    margin: 'auto',
                  }}
                >
                  {areas ? areas.length : 0}
                </Typography>
                <DisplayWithEditAccess>
                  <Divider
                    className={'area-divider'}
                    orientation="vertical"
                    style={{ marginLeft: 2 }}
                  />
                  <TaskCountLabel onClick={handleAddArea}>+ New Area</TaskCountLabel>
                </DisplayWithEditAccess>
              </TaskListCountBox>
            </Grid>
          </Grid>
          <Grid container item xs={5}>
            <Grid item className="value-text" xs={2}>
              <ColorDropdown color={color} disabled={!canEdit} handleSaveColor={handleSaveColor} />
            </Grid>
            <Grid item className="value-text" xs={2}>
              {priority}
            </Grid>
            <Grid item className="value-text" style={{ marginLeft: 'auto' }}>
              <DisplayWithEditAccess>
                <IconButton
                  aria-label="Filter"
                  className="iconImage"
                  disableFocusRipple={true}
                  disableRipple={true}
                  id="more-option-popover"
                  size="small"
                  style={{ padding: 0 }}
                  onClick={handleOpenMoreOptionDialog}
                >
                  <MoreHorizIcon size="small" />
                </IconButton>
                {openMoreOptionDialog && (
                  <MoreOptionDialog
                    anchorEl={openMoreOptionDialog}
                    handleClose={handleCloseMoreOptionDialog}
                    id="more-option-popover"
                    open={openMoreOptionDialog}
                    optionMenu={optionMenu}
                  />
                )}
              </DisplayWithEditAccess>
            </Grid>
          </Grid>
        </Fragment>
      )}
    </RowContainer>
  );
};

export default ZoneRowComponent;
